import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";

const SimplePieChart = ({
  title,
  data,
  labels,
  colors = ["#0288A4", "#17A3AC"],
  width = 280,
  isDonut = false,
  donutWidth = "50%",
  legendPosition = "left",
  showValueOnly = false,
  shows,
  options: extraOptions = {},
  showDataLabels = true,
  showLegendPercentages = false,
  dataLabelsOutside = false,
  totalValue,
}) => {
  const chartRef = useRef(null);
  const outerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const safeData = Array.isArray(data) ? data : [];
  const totalSum = safeData.reduce((acc, curr) => acc + (curr || 0), 0);
  const isZero = totalSum === 0;
  const chartSeries = isZero ? safeData.map(() => 1) : safeData;
  const getPercentage = (index) =>
    totalSum > 0 ? ((safeData[index] || 0) / totalSum) * 100 : 0;
  const externalLabels = dataLabelsOutside && showDataLabels && !isZero;

  // ViewBox dimensions (fixed, mirrors the SVG overlay we draw)
  const VB_W = 300;
  const VB_H = 260;

  // Dynamically measured geometry of the rendered pie in viewBox units
  const [geo, setGeo] = useState({ cx: VB_W / 2, cy: VB_H / 2, radius: 70 });

  const measureChartGeometry = () => {
    // outerRef is the parent of the SVG overlay (absolute inset-0).
    // All pixel→viewBox conversions must be relative to outerRef, not chartRef,
    // otherwise any padding/margin between outerRef and chartRef causes an offset.
    if (!chartRef.current || !outerRef.current) return;
    const outerRect = outerRef.current.getBoundingClientRect();
    if (!outerRect.width || !outerRect.height) return;

    const paths = chartRef.current.querySelectorAll("path.apexcharts-pie-area");
    if (!paths.length) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    paths.forEach((p) => {
      const r = p.getBoundingClientRect();
      minX = Math.min(minX, r.left);
      minY = Math.min(minY, r.top);
      maxX = Math.max(maxX, r.right);
      maxY = Math.max(maxY, r.bottom);
    });
    if (!isFinite(minX)) return;

    // Positions relative to outerRef (SVG coordinate origin)
    const cxPx = (minX + maxX) / 2 - outerRect.left;
    const cyPx = (minY + maxY) / 2 - outerRect.top;
    const radiusPx = (maxX - minX) / 2;

    const scaleX = VB_W / outerRect.width;
    const scaleY = VB_H / outerRect.height;
    const scale = (scaleX + scaleY) / 2;

    setGeo({
      cx: cxPx * scaleX,
      cy: cyPx * scaleY,
      radius: radiusPx * scale,
    });
  };

  const renderLeaderLines = () => {
    if (!externalLabels || totalSum <= 0) return null;

    const { cx, cy, radius } = geo;
    const STUB_LEN = 14;
    const HORIZONTAL_LEN = 40;

    let currentAngle = -90;

    const items = [];
    safeData.forEach((rawVal, index) => {
      const pct = getPercentage(index);
      const sliceAngle = totalSum > 0 ? ((rawVal || 0) / totalSum) * 360 : 0;
      const midAngle = currentAngle + sliceAngle / 2;
      currentAngle += sliceAngle;

      // Skip zero-value slices – no visible arc to attach a line to
      if (!rawVal || rawVal <= 0) return;

      const rad = (midAngle * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      // x1,y1 — exactly on the slice arc edge
      const x1 = cx + radius * cos;
      const y1 = cy + radius * sin;

      // x2,y2 — fixed-length radial stub (14 units, same as PieChart)
      const x2 = x1 + STUB_LEN * cos;
      const y2 = y1 + STUB_LEN * sin;

      const isRight = cos >= 0;

      items.push({
        index,
        val: showValueOnly ? rawVal : `${pct.toFixed(2)}%`,
        color: colors[index % colors.length],
        x1,
        y1,
        x2,
        y2,
        isRight,
        yTarget: y2,
      });
    });

    const rightItems = items
      .filter((it) => it.isRight)
      .sort((a, b) => a.y2 - b.y2);
    const leftItems = items
      .filter((it) => !it.isRight)
      .sort((a, b) => a.y2 - b.y2);

    const applyAntiCollision = (groupList) => {
      const minGap = 18;
      for (let i = 1; i < groupList.length; i++) {
        if (groupList[i].yTarget < groupList[i - 1].yTarget + minGap) {
          groupList[i].yTarget = groupList[i - 1].yTarget + minGap;
        }
      }
      if (
        groupList.length > 0 &&
        groupList[groupList.length - 1].yTarget > 248
      ) {
        groupList[groupList.length - 1].yTarget = 248;
        for (let i = groupList.length - 2; i >= 0; i--) {
          if (groupList[i].yTarget > groupList[i + 1].yTarget - minGap) {
            groupList[i].yTarget = groupList[i + 1].yTarget - minGap;
          }
        }
      }
      if (groupList.length > 0 && groupList[0].yTarget < 12) {
        groupList[0].yTarget = 12;
        for (let i = 1; i < groupList.length; i++) {
          if (groupList[i].yTarget < groupList[i - 1].yTarget + minGap) {
            groupList[i].yTarget = groupList[i - 1].yTarget + minGap;
          }
        }
      }
    };

    applyAntiCollision(rightItems);
    applyAntiCollision(leftItems);

    return (
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: "visible" }}
      >
        {items.map((item) => {
          const { index, val, color, x1, y1, x2, y2, yTarget, isRight } = item;

          // Fixed label column on each side
          const labelColX = isRight ? x2 + HORIZONTAL_LEN : x2 - HORIZONTAL_LEN;

          // 3-point clean L-shape: arc-edge → radial-stub → diagonal to label column
          // No vertical segment = no bracket shape
          const points = `${x1.toFixed(1)},${y1.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)} ${labelColX.toFixed(1)},${yTarget.toFixed(1)}`;

          return (
            <g key={index}>
              <circle cx={x1} cy={y1} r={2.5} fill={color} />
              <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <text
                x={isRight ? labelColX + 4 : labelColX - 4}
                y={yTarget + 4}
                textAnchor={isRight ? "start" : "end"}
                fill="#374151"
                fontSize="11"
                fontWeight="600"
                fontFamily="sans-serif"
              >
                {val}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  const chartOptions = {
    chart: {
      type: isDonut ? "donut" : "pie",
      animations: { enabled: false },
      parentHeightOffset: 0,
      offsetX: 0,
      offsetY: 0,
    },
    labels,
    colors,
    dataLabels: {
      enabled: isZero ? false : showDataLabels && !dataLabelsOutside,
      formatter: (val, { seriesIndex }) =>
        !showValueOnly ? `${val.toFixed(2)}%` : `${data[seriesIndex]}`,
      style: {
        fontSize: "10px",
        fontWeight: "500",
        colors: [dataLabelsOutside ? "#374151" : "#fff"],
      },
      dropShadow: { enabled: false },
    },
    legend: { show: false },
    plotOptions: {
      pie: {
        expandOnClick: false,
        size: "85%",
        offsetX: 0,
        offsetY: 0,
        donut: {
          size: isDonut ? donutWidth : "0%",
          labels: {
            show: shows || false,
            name: {
              show: true,
              fontSize: "11px",
              fontWeight: 600,
              color: undefined,
              offsetY: -3,
            },
            value: {
              show: true,
              fontSize: "15px",
              fontWeight: 700,
              offsetY: 7,
              formatter: (val, opts) => {
                if (!opts) return isZero ? "0" : val;
                const idx = opts?.config?.series ? opts.seriesIndex : undefined;
                if (isZero) return "0";
                return idx !== undefined && safeData[idx] !== undefined
                  ? `${safeData[idx]}`
                  : val;
              },
            },
            total: {
              show: true,
              label: "Total",
              fontSize: "12px",
              fontWeight: 600,
              formatter: () => {
                if (totalValue !== undefined) return `${totalValue}`;
                if (isZero) return "0";
                const sum = safeData.reduce((a, b) => a + b, 0);
                return Number.isInteger(sum) ? `${sum}` : `${sum.toFixed(2)}`;
              },
            },
          },
        },
      },
    },
    stroke: { show: false, width: 0 },
    tooltip: {
      y: {
        formatter: (val, { seriesIndex }) =>
          isZero ? "0" : `${safeData[seriesIndex] ?? val}`,
      },
    },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: { width: "100%" },
          dataLabels: { style: { fontSize: "9px" } },
        },
      },
      {
        breakpoint: 768,
        options: {
          chart: { width: "100%" },
          dataLabels: { style: { fontSize: "8.5px" } },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: { width: "100%" },
          plotOptions: {
            pie: { size: "75%", donut: { size: isDonut ? "45%" : "0%" } },
          },
          dataLabels: { style: { fontSize: "8px" } },
        },
      },
    ],
    ...extraOptions,
  };

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = new ApexCharts(chartRef.current, {
      ...chartOptions,
      series: chartSeries,
    });
    chartInstanceRef.current = chart;
    chart.render().then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(measureChartGeometry);
      });
    });
    return () => chart.destroy();
  }, [data, labels]);

  useEffect(() => {
    const handleResize = () => requestAnimationFrame(measureChartGeometry);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="w-full max-w-full px-2 sm:px-4">
      {/* Title */}
      {title && (
        <div className="text-center mb-4">
          <h3 className="text-base sm:text-lg font-semibold">{title}</h3>
        </div>
      )}

      {/* Chart and Legend */}
      <div
        className={`flex flex-col-reverse 2xl:flex-row ${
          legendPosition === "right" ? "2xl:flex-row-reverse" : "2xl:flex-row"
        } items-center justify-center gap-4 2xl:gap-6`}
      >
        {/* Legend */}
        <div className="flex flex-row flex-wrap 2xl:flex-col gap-2 justify-center 2xl:justify-start 2xl:ml-6 ml-0">
          {labels.map((label, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="w-3 h-3 flex-shrink-0 rounded-sm"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="text-xs 2xl:text-sm font-medium text-gray-700 truncate">
                {label}
                {showLegendPercentages &&
                  ` (${getPercentage(index).toFixed(2)}%)`}
              </span>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div
          ref={outerRef}
          className="relative w-full 2xl:w-auto max-w-xs 2xl:max-w-sm overflow-visible"
        >
          <div ref={chartRef} className="w-full" />
          {renderLeaderLines()}
        </div>
      </div>
    </div>
  );
};

export default SimplePieChart;
